/**
 * Vincent B795 (Eric 2026-02-12) — Modale de recadrage de photo.
 *
 * Contexte : Vincent demande de pouvoir recentrer et recadrer la photo
 * d'un locuteur, sans avoir à ré-uploader le fichier. Cette modale
 * permet de définir un carré de recadrage (drag + zoom) sur la photo
 * SOURCE et sauvegarde les paramètres normalisés {cx, cy, size} via
 * l'endpoint `PATCH /api/speakers/{id}/photo-crop`.
 *
 * Le rendu recadré est ensuite obtenu côté serveur via
 * `GET /api/speakers/{id}/photo?cropped=1` (Pillow crop + resize 512×512).
 *
 * Composant réutilisable :
 *  - `sourceUrl` : URL de l'image SOURCE (non recadrée)
 *  - `initialCrop` : {cx, cy, size} pré-chargé depuis le speaker, ou null
 *  - `onSave(crop)` : appelé avec les nouveaux paramètres normalisés
 *  - `onCancel()` : ferme sans sauvegarder
 *
 * Aucune dépendance externe (react-easy-crop, etc.) — canvas HTML5 pur.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Camera, X, Check, ArrowsOutCardinal } from "@phosphor-icons/react";

const PREVIEW_SIZE = 400; // Taille du conteneur d'aperçu en pixels.
const OUTPUT_PREVIEW_SIZE = 96; // Taille de l'aperçu final (cercle).

export default function PhotoCropModal({
  sourceUrl,
  initialCrop,
  onSave,
  onCancel,
  speakerName,
  lang = "fr",
}) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgDims, setImgDims] = useState({ w: 0, h: 0 });
  // État du crop en coordonnées normalisées (0-1).
  const [crop, setCrop] = useState(() => ({
    cx: initialCrop?.cx ?? 0.5,
    cy: initialCrop?.cy ?? 0.5,
    size: initialCrop?.size ?? 1.0,
  }));
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const dragRef = useRef(null);

  // Calcule les dimensions d'affichage de l'image dans le conteneur
  // PREVIEW_SIZE × PREVIEW_SIZE (contient, préserve le ratio).
  const displayDims = useMemo(() => {
    if (!imgDims.w || !imgDims.h) return { w: PREVIEW_SIZE, h: PREVIEW_SIZE, offsetX: 0, offsetY: 0 };
    const ratio = imgDims.w / imgDims.h;
    let w, h;
    if (ratio > 1) {
      w = PREVIEW_SIZE;
      h = PREVIEW_SIZE / ratio;
    } else {
      h = PREVIEW_SIZE;
      w = PREVIEW_SIZE * ratio;
    }
    return {
      w,
      h,
      offsetX: (PREVIEW_SIZE - w) / 2,
      offsetY: (PREVIEW_SIZE - h) / 2,
    };
  }, [imgDims]);

  // Position et taille du carré de crop en pixels (dans le conteneur).
  const cropRect = useMemo(() => {
    const minSide = Math.min(displayDims.w, displayDims.h);
    const sidePx = crop.size * minSide;
    const cxPx = displayDims.offsetX + crop.cx * displayDims.w;
    const cyPx = displayDims.offsetY + crop.cy * displayDims.h;
    return {
      side: sidePx,
      left: cxPx - sidePx / 2,
      top: cyPx - sidePx / 2,
    };
  }, [crop, displayDims]);

  const handleImgLoad = (e) => {
    setImgDims({ w: e.target.naturalWidth, h: e.target.naturalHeight });
    setImgLoaded(true);
  };

  // Drag pour déplacer le centre du crop.
  const startDrag = useCallback((e) => {
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const startX = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const startY = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    dragRef.current = {
      startX,
      startY,
      startCx: crop.cx,
      startCy: crop.cy,
    };
  }, [crop]);

  const onDrag = useCallback((e) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const dxPx = x - dragRef.current.startX;
    const dyPx = y - dragRef.current.startY;
    // Convertit en coordonnées normalisées de l'image.
    const dxNorm = displayDims.w > 0 ? dxPx / displayDims.w : 0;
    const dyNorm = displayDims.h > 0 ? dyPx / displayDims.h : 0;
    // Bornes pour rester dans l'image (marge dépend de la taille du crop).
    const halfSizeNormX = (crop.size * Math.min(displayDims.w, displayDims.h)) / (2 * displayDims.w);
    const halfSizeNormY = (crop.size * Math.min(displayDims.w, displayDims.h)) / (2 * displayDims.h);
    const nextCx = Math.max(halfSizeNormX, Math.min(1 - halfSizeNormX, dragRef.current.startCx + dxNorm));
    const nextCy = Math.max(halfSizeNormY, Math.min(1 - halfSizeNormY, dragRef.current.startCy + dyNorm));
    setCrop((c) => ({ ...c, cx: nextCx, cy: nextCy }));
  }, [crop.size, displayDims]);

  const stopDrag = useCallback(() => {
    dragRef.current = null;
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("touchmove", onDrag, { passive: false });
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchend", stopDrag);
    return () => {
      window.removeEventListener("mousemove", onDrag);
      window.removeEventListener("touchmove", onDrag);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchend", stopDrag);
    };
  }, [onDrag, stopDrag]);

  const handleZoomChange = (newSize) => {
    // Reclampe le centre pour rester dans l'image après changement de taille.
    setCrop((c) => {
      const halfSizeNormX = (newSize * Math.min(displayDims.w, displayDims.h)) / (2 * displayDims.w);
      const halfSizeNormY = (newSize * Math.min(displayDims.w, displayDims.h)) / (2 * displayDims.h);
      return {
        cx: Math.max(halfSizeNormX, Math.min(1 - halfSizeNormX, c.cx)),
        cy: Math.max(halfSizeNormY, Math.min(1 - halfSizeNormY, c.cy)),
        size: newSize,
      };
    });
  };

  const handleReset = () => {
    setCrop({ cx: 0.5, cy: 0.5, size: 1.0 });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(crop);
    } finally {
      setSaving(false);
    }
  };

  // Aperçu final : dessine un crop dans un mini-canvas cercle.
  const previewClipPath = `inset(0 round 50%)`;
  const previewImgStyle = useMemo(() => {
    if (!imgDims.w || !imgDims.h) return { display: "none" };
    // Pour afficher le crop de manière équivalente à ce que le serveur fera :
    // On projette la source dans un carré de taille OUTPUT_PREVIEW_SIZE avec
    // object-fit: cover + object-position calé sur (cx, cy) + scale zoom.
    const minSide = Math.min(imgDims.w, imgDims.h);
    const scale = minSide / (crop.size * minSide);
    return {
      width: OUTPUT_PREVIEW_SIZE,
      height: OUTPUT_PREVIEW_SIZE,
      objectFit: "cover",
      objectPosition: `${crop.cx * 100}% ${crop.cy * 100}%`,
      transform: `scale(${scale})`,
      transformOrigin: `${crop.cx * 100}% ${crop.cy * 100}%`,
    };
  }, [crop, imgDims]);

  const zoomPct = Math.round((1 / crop.size) * 100);

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
      data-testid="photo-crop-modal"
      role="dialog"
      aria-modal="true"
      aria-label={lang === "fr" ? "Recadrer la photo" : "Crop photo"}
    >
      <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-2">
            <Camera size={18} className="text-blue-600" />
            <h2 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
              {lang === "fr" ? "Recadrer la photo" : "Crop photo"}
              {speakerName && (
                <span className="ml-1 text-zinc-500 font-normal">— {speakerName}</span>
              )}
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded text-zinc-500"
            data-testid="photo-crop-close-btn"
            aria-label={lang === "fr" ? "Fermer" : "Close"}
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="flex flex-col md:flex-row gap-5 items-start">
            {/* Zone d'ajustement */}
            <div className="flex-1 flex flex-col items-center">
              <div
                ref={containerRef}
                className="relative bg-zinc-100 dark:bg-zinc-800 rounded-lg overflow-hidden select-none"
                style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE, maxWidth: "100%" }}
                data-testid="photo-crop-canvas"
              >
                {sourceUrl && (
                  <img
                    ref={imgRef}
                    src={sourceUrl}
                    alt={lang === "fr" ? "À recadrer" : "To crop"}
                    onLoad={handleImgLoad}
                    className="absolute pointer-events-none"
                    style={{
                      width: displayDims.w,
                      height: displayDims.h,
                      left: displayDims.offsetX,
                      top: displayDims.offsetY,
                    }}
                    draggable={false}
                    data-testid="photo-crop-source-img"
                  />
                )}
                {imgLoaded && (
                  <>
                    {/* Masque assombri hors zone de crop (4 rectangles) */}
                    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                      <div className="absolute bg-black/50" style={{ left: 0, top: 0, right: 0, height: cropRect.top }} />
                      <div className="absolute bg-black/50" style={{ left: 0, top: cropRect.top + cropRect.side, right: 0, bottom: 0 }} />
                      <div className="absolute bg-black/50" style={{ left: 0, top: cropRect.top, width: cropRect.left, height: cropRect.side }} />
                      <div className="absolute bg-black/50" style={{ left: cropRect.left + cropRect.side, top: cropRect.top, right: 0, height: cropRect.side }} />
                    </div>
                    {/* Cadre de crop draggable (avec cercle inscrit pour visualiser l'avatar final) */}
                    <div
                      onMouseDown={startDrag}
                      onTouchStart={startDrag}
                      className="absolute border-2 border-white shadow-lg cursor-move"
                      style={{
                        left: cropRect.left,
                        top: cropRect.top,
                        width: cropRect.side,
                        height: cropRect.side,
                      }}
                      data-testid="photo-crop-frame"
                    >
                      <div
                        className="absolute inset-0 border-2 border-white/60 rounded-full pointer-events-none"
                        aria-hidden="true"
                      />
                      <ArrowsOutCardinal
                        size={20}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/80 pointer-events-none"
                        aria-hidden="true"
                      />
                    </div>
                  </>
                )}
              </div>
              {/* Zoom slider */}
              <div className="w-full mt-4 flex items-center gap-3">
                <span className="text-xs text-zinc-500 font-medium w-14">
                  {lang === "fr" ? "Zoom" : "Zoom"}
                </span>
                <input
                  type="range"
                  min="0.2"
                  max="1.0"
                  step="0.01"
                  // Note : plus `size` est PETIT, plus on zoome. Le slider est
                  // donc inversé visuellement pour être intuitif (droite = +
                  // zoom). On mappe : slider 0 → size 1.0, slider 100 → size 0.2.
                  value={1.2 - crop.size}
                  onChange={(e) => handleZoomChange(Math.max(0.2, Math.min(1.0, 1.2 - parseFloat(e.target.value))))}
                  className="flex-1 accent-blue-600"
                  data-testid="photo-crop-zoom-slider"
                  aria-label={lang === "fr" ? "Zoom" : "Zoom"}
                />
                <span className="text-xs text-zinc-500 font-mono w-12 text-right" data-testid="photo-crop-zoom-value">
                  {zoomPct}%
                </span>
              </div>
            </div>

            {/* Aperçu du rendu final */}
            <div className="flex flex-col items-center gap-2 md:pt-4">
              <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                {lang === "fr" ? "Aperçu" : "Preview"}
              </span>
              <div
                className="rounded-full overflow-hidden border-2 border-violet-500/40 bg-zinc-100 dark:bg-zinc-800"
                style={{ width: OUTPUT_PREVIEW_SIZE, height: OUTPUT_PREVIEW_SIZE, clipPath: previewClipPath }}
                data-testid="photo-crop-preview"
              >
                {sourceUrl && imgLoaded && (
                  <img
                    src={sourceUrl}
                    alt=""
                    style={previewImgStyle}
                    draggable={false}
                  />
                )}
              </div>
              <button
                onClick={handleReset}
                className="text-[11px] text-blue-600 hover:underline mt-1"
                data-testid="photo-crop-reset-btn"
                type="button"
              >
                {lang === "fr" ? "Réinitialiser" : "Reset"}
              </button>
            </div>
          </div>

          <p className="text-[11px] text-zinc-500 italic text-center">
            {lang === "fr"
              ? "Glissez le cadre pour recentrer, utilisez le slider pour zoomer."
              : "Drag the frame to recenter, use the slider to zoom."}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-200 dark:border-zinc-700">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-1.5 rounded-md border border-zinc-300 text-zinc-700 dark:text-zinc-200 text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50"
            data-testid="photo-crop-cancel-btn"
            type="button"
          >
            {lang === "fr" ? "Annuler" : "Cancel"}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !imgLoaded}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="photo-crop-save-btn"
            type="button"
          >
            <Check size={14} weight="bold" />
            {saving ? (lang === "fr" ? "Enregistrement…" : "Saving…") : (lang === "fr" ? "Valider" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
}
